import { autoinject, transient, computedFrom, useView } from "aurelia-framework";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { AttachmentClientService } from "../../../../../../services/attachment/attachments-client.service";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { Claims } from "../../../../../../constants/authentication/claims";
import { RightshipEntityAttachmentModel } from "../../../../../../models/attachment/attachment.models";
import { RightshipConfirmationDialogModel } from "../../../../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { RightshipEntityAttachmentManager } from "../../../common-steps/rightship-entity-attachment/rightship-entity-attachment-manager";
import { BindingSignaler } from "aurelia-templating-resources";
import { QueriesClientService } from "../../../../../../services/queries/queries-client.services";
import { VoyageDataSubmissionClientModel, VoyageDataSubmissionStatusEnum } from "../../../../../../models/vetting/vetting.models";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { SignalFunctions } from "../../../../../../services/basics/signal-functions";
import { VoyageDataSubmissionService } from "../../../../../../services/vetting/voyage-data-submission.service";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { EntityAttachmentDialogModel } from "../../../common-steps/dialogs/entity-attachment/rightship-entity-attachment-dialog";

@autoinject
@transient()
@useView('views/pages/modules/voyage-data-submission/steps/documents/voyage-data-submission-documents.html')
export class VoyageDataSubmissionDocuments extends RightshipEntityAttachmentManager {
    public multiple: boolean = true;
    public accept: string = "application/pdf,image/png,image/jpeg,image/jpg,application/zip,application/x-zip-compressed,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.ms-outlook,.msg";

    public showAttachmentTypes: boolean = true;
    public showCulture: boolean = true;

    canAccessUploadFileButtonAfterStatusCompleted: boolean;

    constructor(attachmentClientService: AttachmentClientService, notificationService: NotificationClientService, userClaimsService: UserClaimsService,
        dialogService: DialogClientService, i18N: I18N, queriesClientService: QueriesClientService, private validationsClientService: ValidationsClientService,
        bindingSignaler: BindingSignaler, private signalService: SignalClientService, private voyageDataSybmissionService: VoyageDataSubmissionService) {
        super(attachmentClientService, notificationService, userClaimsService, dialogService, i18N, queriesClientService, bindingSignaler);
        this.canAccessUploadFileButtonAfterStatusCompleted = this.userClaimsService.hasPermission(Claims.CanAccessUploadFileButtonAfterStatusCompleted)
    }

    @computedFrom("rootItem", "rootItem.voyageDataSubmissionStatusId")
    public get statusIsFinal(): boolean {
        return this.rootItem && (this.rootItem["voyageDataSubmissionStatusId"] == VoyageDataSubmissionStatusEnum.Cancelled || this.rootItem["voyageDataSubmissionStatusId"] == VoyageDataSubmissionStatusEnum.ReadyForReporting || this.rootItem["voyageDataSubmissionStatusId"] == VoyageDataSubmissionStatusEnum.Completed);
    }

    @computedFrom("rootItem", "rootItem.voyageDataSubmissionStatusId", "canAccessUploadFileButtonAfterStatusCompleted")
    public get canAddAfterComplete(): boolean {
        return this.rootItem && (this.rootItem["voyageDataSubmissionStatusId"] == VoyageDataSubmissionStatusEnum.ReadyForReporting || this.rootItem["voyageDataSubmissionStatusId"] == VoyageDataSubmissionStatusEnum.Completed) && this.canAccessUploadFileButtonAfterStatusCompleted;
    }

    public openAttachmentsDialog() {
        this.itemInEdit = null;
        this.dialogService.show(RightShipDialogServiceConstants.URLs.RightshipAttachmentsDialog, this, true, false, false, 'right');
    }

    @computedFrom('rootItem', 'getItemId')
    public get canAdd(): boolean {
        return super.canAdd && (this.entityId && this.entityId > 0) && this.userClaimsService.hasPermission(Claims.CanAddCarbonAccountingDocuments);
    }

    @computedFrom('rootItem', 'getItemId')
    public get canDelete(): boolean {
        return super.canDelete && (this.entityId && this.entityId > 0) && this.userClaimsService.hasPermission(Claims.CanRemoveCarbonAccountingDocuments);
    }

    attached() {
        this.signalService.subscribe(SignalFunctions.voyageLegDataAttachmentParsedChanged, this.reloadAttachments);
        this.signalService.subscribe(SignalFunctions.voyageDataExcelsGenerated, this.reloadAttachments);
    }

    detached() {
        this.signalService.unsubscribe(SignalFunctions.voyageLegDataAttachmentParsedChanged, this.reloadAttachments);
        this.signalService.unsubscribe(SignalFunctions.voyageDataExcelsGenerated, this.reloadAttachments);
    }

    public remove($event, item: RightshipEntityAttachmentModel) {
        $event.stopPropagation();
        const deleteItem = item;

        const model = new RightshipConfirmationDialogModel();
        model.confirm = this.i18N.tr('delete-text');
        model.cancel = this.i18N.tr('cancel-text');
        model.messages.push(this.i18N.tr('are-you-sure-you-want-to-delete'));
        model.title = this.i18N.tr('confirm-delete-text');
        model.onConfirm = () => {
            this.removeInternal($event, deleteItem).then(() => {
                this.notificationService.show(this.i18N.tr("attachment-was-deleted-text"), "", NotificationType.Success);
                this.dialogService.hide();
            }).catch(() => {
                this.notificationService.show(this.i18N.tr("error-text") + "!", this.i18N.tr("attachment-could-not-be-deleted-text"), NotificationType.Error);
                this.dialogService.hide();
            });
        };
        this.dialogService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, false, false);

    }

    private reloadAttachments = (data) => {
        if (data && this.rootItem && data.id == (<VoyageDataSubmissionClientModel>this.rootItem).id) {
            if (this.loadAttachments) {
                this.loadAttachments();
            }
        }
    }

    public loadAttachments() {
        return new Promise<void>((resolve, reject) => {
            if (this.isLoadingAttachments || !this.shouldLoadAttachments) {
                resolve();
                return;
            }

            this.isLoadingAttachments = true;

            this.items = [];

            this.queriesClientService.getData('odata/RightShipAttachments/GetAttachments(entityTypeId=' + this.rootEntityType + ',entityId=' + (<VoyageDataSubmissionClientModel>this.rootItem).id + ')').then(results => {
                this.items = results.value
                if (this.onAttachmentsLoaded) {
                    this.onAttachmentsLoaded();
                }
                this.isLoadingAttachments = false;
                (<VoyageDataSubmissionClientModel>this.rootItem).documentsCount = this.items && this.items.length ? this.items.length : 0;
                this.bindingSignaler.signal('update-title');
                resolve();
            }).catch(() => {
                this.isLoadingAttachments = false;
                reject();
            });
        });
    }

    public beforeSaveFunc: (item: any, files: File[], filesDetails: EntityAttachmentDialogModel[], cultureCode?: string) => Promise<boolean> = 
        (item: any, files: File[], filesDetails: EntityAttachmentDialogModel[], cultureCode?: string) =>
    {
        return new Promise<boolean>((resolve) => {
            const attachmentDetails = filesDetails.map(x => {
                return {
                    attachmentTypeId: x.attachmentTypeId,
                    name: x.name
                }
            });
            this.voyageDataSybmissionService.validateAttachments(item.id, files, attachmentDetails, cultureCode).then(results => {
                if(!results || results.length == 0 ) 
                    resolve(true);
                else 
                {
                    const message = results.map(x => `Cell ${x["columnName"]} ${x["rowNumber"]} - ${x["validationError"]}`).join("<br />\n");
                    this.notificationService.show("Validation Errors!", message, NotificationType.Error);
                    resolve(false);
                }
            }).catch(e => {
                console.error(e);
                if (e.content && e.content.validationResult && e.content.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(e.content.validationResult);
                }
                else this.notificationService.show("Validation Failed!", "", NotificationType.Error);
                resolve(false);
            })
        });
    }
}

export interface EntityAttachmentDetailsModel{
    attachmentTypeId?:number,
    name:string
}
